/**
 * The two dialogs this app asks for: a confirmation, and a prompt.
 *
 * `confirm` wraps Webwin's `messageBox`, which is what replaces the 31 `MessageBox.Show` calls in
 * the OpenSilver client. `ask` replaces `window.prompt`, which the file explorer and the network page
 * used because OpenSilver had no input dialog - and which a canvas app cannot use at all, since a
 * native prompt blocks the whole page including the render loop.
 *
 * BOTH RESOLVE, NEVER THROW. A cancelled dialog is an ordinary answer ("no", null), because a caller
 * that has to try/catch a cancellation writes the same wrong thing every time: a status line saying
 * something went wrong when nothing did.
 */
import {
  Dialog,
  insets,
  messageBox,
  TextBox,
  type Desktop
} from 'webwin';
import { CrmLabel } from './Text.js';
import { button, FONT, GAP, hstack, stackOf, textBox, vstack } from './layout.js';

export interface ConfirmOptions {
  title: string;
  /**
   * The whole consequence, not a restatement of the button.
   *
   * The OpenSilver confirmations are worth copying verbatim where they exist: "Archive Acme? Its
   * people, devices, tickets, work and invoices are all kept..." is a sentence someone wrote because
   * the two effects that matter - agents standing down, portal logins stopping - are invisible from
   * the button.
   */
  message: string;
  /** Defaults to OK / Cancel, which is what every confirmation in the OpenSilver app used. */
  okLabel?: 'ok' | 'yes';
  width?: number;
}

/** True when the person said yes. */
export async function confirm(desktop: Desktop, options: ConfirmOptions): Promise<boolean> {
  const yes = options.okLabel ?? 'ok';
  const no = yes === 'yes' ? 'no' : 'cancel';
  const answer = await messageBox(desktop, {
    title: options.title,
    message: options.message,
    buttons: [yes, no],
    defaultResult: no, // Enter must not be the destructive one.
    cancelResult: no,
    width: options.width ?? 460
  });
  return answer === yes;
}

/** A plain "something went wrong" / "here is what happened" box, with one button. */
export function tell(desktop: Desktop, title: string, message: string): Promise<unknown> {
  return messageBox(desktop, { title, message, buttons: ['ok'], width: 460 });
}

export interface AskOptions {
  title: string;
  /** What is being asked for. Shown above the box. */
  prompt: string;
  initial?: string;
  okLabel?: string;
  width?: number;
}

/**
 * Asks for one line of text. Null when cancelled.
 *
 * Hand-built rather than a `messageBox` variant because the field has to be focused and selected
 * when the dialog opens and Enter has to submit - a prompt that needs a click into its own box
 * before it can be typed into is slower than the `window.prompt` it replaces.
 */
export async function ask(desktop: Desktop, options: AskOptions): Promise<string | null> {
  const width = options.width ?? 420;
  const height = 170;
  const dialog = new Dialog({ title: options.title, width, height, visible: false });

  // Attached first, and the client size set afterwards: `clientRect` comes from the theme's frame
  // metrics, so a detached window does not have one and a `dock: 'fill'` child would size to nothing.
  desktop.addWindow(dialog);
  dialog.setClientSize(width, height);

  const input: TextBox = textBox({ text: options.initial ?? '' });
  const body = stackOf(
    vstack({ spacing: GAP.md, padding: insets(GAP.lg), dock: 'fill', autoHeight: false, autoSize: false }),
    new CrmLabel({
      text: options.prompt,
      font: FONT.body,
      wrap: true,
      autoHeight: true,
      hAlign: 'stretch'
    }),
    input
  );

  const buttons = hstack({ spacing: GAP.sm, hAlign: 'end' });
  buttons.controls.add(button('Cancel', () => dialog.closeWith('cancel')));
  buttons.controls.add(button(options.okLabel ?? 'OK', () => dialog.closeWith('ok'), true));
  body.controls.add(buttons);

  input.on('keydown', (e: { key: string }) => {
    if (e.key === 'Enter') dialog.closeWith('ok');
    else if (e.key === 'Escape') dialog.closeWith('cancel');
  });

  dialog.controls.add(body);
  dialog.layout();

  const shown = dialog.showDialog(desktop);
  // After `showDialog`, which restores focus into the window itself - focusing before it would be
  // undone, and the point of this dialog over `window.prompt` is that it opens ready to type into.
  input.focus();
  input.selectAll();

  return (await shown) === 'ok' ? input.text : null;
}
